// 📄 Tools.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Table,
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    CustomInput,
    Label,
    Spinner,
    Badge
} from 'reactstrap';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import { useHistory } from 'react-router-dom';

import downloadService from 'services/toolUpload.service';
import toolService from 'services/tools.service';

function Tools() {
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    // Base URL của folder chứa file (theo cấu hình server)
    const baseURL = "https://download.dhbtools.com/download/";

    // Hàm format ngày theo định dạng dd/mm/yyyy HH:mm:ss
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const HH = String(date.getHours()).padStart(2, '0');
        const MM = String(date.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${HH}:${MM}`;
    };

    /**
     * 🕐 Format relative time
     */
    function formatRelativeTime(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Vừa xong';
        if (diffMins < 60) return `${diffMins} phút trước`;
        if (diffHours < 24) return `${diffHours} giờ trước`;
        if (diffDays < 7) return `${diffDays} ngày trước`;
        return '';
    }

    useEffect(() => {
        loadTools();
    }, []);

    function loadTools() {
        setLoading(true);
        toolService
            .getToolDatabase()
            .then((result) => {
                setTools(result.data);
            })
            .catch((error) => {
                Swal.fire('Lỗi!', `Không thể tải dữ liệu <br><br> ${error}`, 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // Hàm chuyển đến trang chi tiết công cụ
    const navigateToToolDetails = (tool) => {
        history.push(`/admin/tools/${tool.matool}`);
    };

    // Hàm thay đổi trạng thái xác thực
    const handleSwitchChange = (e, tool) => {
        e.stopPropagation(); // Ngăn việc click lan ra row
        const newValue = !tool.downloadRequireAuth;
        downloadService
            .updateRequireAuthen(tool.id, { downloadRequireAuth: newValue })
            .then(() => {
                setTools((prevTools) =>
                    prevTools.map((t) =>
                        t.id === tool.id ? { ...t, downloadRequireAuth: newValue } : t
                    )
                );
            })
            .catch((error) => {
                console.error('Lỗi khi cập nhật xác thực:', error);
                Swal.fire('Lỗi!', `Cập nhật xác thực thất bại: ${error}`, 'error');
            });
    };

    const handleCopyUrl = async (e, tool) => {
        e.stopPropagation();
        try {
            const response = await downloadService.getToken(tool.matool);
            const token = response.token;
            const fileUrl = `${baseURL}${tool.matool}?token=${token}`;

            Swal.fire({
                title: 'Sao chép URL',
                html: `
                     <div margin-top: 10px;>
                        <textarea id="file-url" readonly style="width: 100%; height: 150px; padding: 10px; font-size: 14px; resize: none; border-radius: 12px; border: 1px solid #e0e0e0;">${fileUrl}</textarea>
                        <p style="margin-bottom: 10px; margin-top: 10px; color: #8898aa;">Lưu ý: URL có hiệu lực 1 giờ sau khi tạo</p>
                        <button id="copy-url-btn" style="margin-top: 10px; padding: 8px 20px; background-color: #28a745; color: white; border: none; border-radius: 20px; cursor: pointer; font-weight: 600;">
                            Sao chép
                        </button>
                    </div>
                `,
                showConfirmButton: false,
                didOpen: () => {
                    const copyButton = Swal.getHtmlContainer()?.querySelector('#copy-url-btn');
                    if (copyButton) {
                        copyButton.addEventListener('click', async () => {
                            try {
                                await navigator.clipboard.writeText(fileUrl);
                                Swal.fire('Thành công!', 'URL đã được sao chép vào clipboard.', 'success');
                            } catch (error) {
                                Swal.fire('Lỗi!', 'Không thể sao chép URL.', 'error');
                            }
                        });
                    }
                }
            });
        } catch (error) {
            Swal.fire('Lỗi!', 'Không thể lấy URL.', 'error');
        }
    };

    // Tham chiếu đến input file cho từng hàng
    const fileInputRefs = useRef({});

    // Khi bấm "Tải lên", kích hoạt input file ẩn của tool đó
    const handleUploadClick = (e, toolId) => {
        e.stopPropagation();
        if (fileInputRefs.current[toolId]) {
            fileInputRefs.current[toolId].click();
        }
    };

    // Khi người dùng chọn file để tải lên
    const handleFileChange = (tool) => async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        Swal.fire({
            title: 'Đang tải lên...',
            html: `
                <div id="progress-container" style="width:100%;background: #eee; border-radius: 20px; overflow: hidden;">
                    <div id="progress-bar" style="width:0%;height:20px;background: linear-gradient(90deg, #28a745, #20c997); border-radius: 20px; transition: width 0.3s;"></div>
                </div>
                <div id="progress-percent" style="margin-top:10px; font-weight: 600; color: #2c3e50;">0%</div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            await downloadService.uploadFileChunked(tool.id, file, (progress) => {
                const progressBar = Swal.getHtmlContainer()?.querySelector('#progress-bar');
                const progressPercent = Swal.getHtmlContainer()?.querySelector('#progress-percent');
                if (progressBar) progressBar.style.width = progress.progress + '%';
                if (progressPercent) progressPercent.innerText = progress.progress + '%';
            });
            Swal.fire('Thành công!', 'Tải file lên thành công!', 'success');
            loadTools();
        } catch (error) {
            Swal.fire('Lỗi!', `Tải file thất bại: ${error}`, 'error');
        }
    };

    // Shared styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'hidden',
        margin: '0 auto'
    };

    const thStyle = {
        border: 'none',
        fontSize: '14px',
        color: '#8898aa',
        fontWeight: '600',
        textTransform: 'uppercase'
    };

    return (
        <div className="content">
            <Card style={cardStyle}>
                <CardHeader style={{
                    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    padding: '20px 24px',
                    borderBottom: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i className="fas fa-cube" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                        <div style={{ lineHeight: '1.3' }}>
                            <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                                Quản lý phần mềm
                            </Label>
                            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                                {tools.length > 0 ? `${tools.length} phần mềm` : 'Đang tải...'}
                            </span>
                        </div>
                    </div>
                    <Button
                        onClick={loadTools}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            color: '#fff',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}
                    >
                        <i className="fas fa-sync-alt" style={{ marginRight: '6px' }}></i> Refresh
                    </Button>
                </CardHeader>
                <CardBody style={{ padding: '16px 24px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <Table style={{ marginTop: '10px', marginBottom: 0 }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                    <th style={thStyle}>Phần mềm</th>
                                    <th style={thStyle}>Tệp tin</th>
                                    <th style={thStyle}>Mật khẩu</th>
                                    <th style={thStyle}>Người tải</th>
                                    <th style={thStyle}>Ngày tải lên</th>
                                    <th style={thStyle}>Xác thực</th>
                                    <th style={thStyle}>URL</th>
                                    <th style={thStyle}>Tải lên</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tools.map((tool, index) => {
                                    const relative = formatRelativeTime(tool.updatedAt);
                                    return (
                                        <tr
                                            key={tool.id || index}
                                            onClick={() => navigateToToolDetails(tool)}
                                            style={{
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f5f5f5',
                                                transition: 'background-color 0.2s ease'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                                <span style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                                                    {tool.name}
                                                </span>
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f' }}>
                                                <span style={{
                                                    padding: '3px 10px',
                                                    borderRadius: '20px',
                                                    backgroundColor: 'rgba(81,203,206,0.1)',
                                                    fontSize: '14px',
                                                    fontWeight: '500'
                                                }}>
                                                    {tool.file}
                                                </span>
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#8898aa' }}>
                                                {tool.passunrar || <span style={{ color: '#d4d4d4' }}>—</span>}
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f' }}>
                                                {tool.uploadedBy}
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontSize: '15px', color: '#2c3e50' }}>
                                                    {formatDate(tool.updatedAt)}
                                                </div>
                                                {relative && (
                                                    <div style={{ fontSize: '13px', color: '#51cbce', fontWeight: '500' }}>
                                                        {relative}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                                                <CustomInput
                                                    type="switch"
                                                    id={`switch-${tool.id}`}
                                                    checked={!!tool.downloadRequireAuth}
                                                    onChange={(e) => handleSwitchChange(e, tool)}
                                                />
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                                <Button
                                                    color="info"
                                                    size="sm"
                                                    onClick={(e) => handleCopyUrl(e, tool)}
                                                    style={{ borderRadius: '20px', fontSize: '13px', padding: '4px 12px' }}
                                                >
                                                    <i className="fas fa-copy" style={{ marginRight: '4px' }} /> URL
                                                </Button>
                                            </td>
                                            <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                                <input
                                                    type="file"
                                                    ref={(ref) => (fileInputRefs.current[tool.id] = ref)}
                                                    style={{ display: 'none' }}
                                                    onChange={handleFileChange(tool)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    onClick={(e) => handleUploadClick(e, tool.id)}
                                                    style={{ borderRadius: '20px', fontSize: '13px', padding: '4px 12px' }}
                                                >
                                                    <i className="fas fa-cloud-upload-alt" style={{ marginRight: '4px' }} /> Tải lên
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

export default Tools;
